import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  InteractionStatus,
  SilentRequest,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { useDispatch, useSelector } from "react-redux";
import { loginRequest } from "~/packages/msalInstance";
import api from "~/api";
import { LinearProgress } from "@material-ui/core";
import { toast } from "react-toastify";
import { loggedIn, setUserData } from "~/store/auth/actions";

interface Props { children?: React.ReactNode; }

const AuthProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const { accounts, instance, inProgress } = useMsal();
  const account: AccountInfo | undefined = accounts[0];
  const [loading, setLoading] = useState(true);
  const connectPromiseRef = useRef<Promise<void> | null>(null);
  const authData = useSelector((s: ReduxStore.State) => s.auth.authData);


  const acquireTokens = useCallback(async (): Promise<AuthenticationResult> => {
    if (!account) throw new Error("No MSAL account");
    if (inProgress !== InteractionStatus.None) throw new Error("MSAL interaction in progress");
    return instance.acquireTokenSilent({ ...(loginRequest as SilentRequest), account });
  }, [account, inProgress, instance]);

  const connect = useCallback(async () => {
    if (!account) return;
    if (connectPromiseRef.current) return;

    setLoading(true);
    const connectPromise = (async () => {
      let shouldStopLoading = true;
      try {
        const res = await acquireTokens();
        const azureToken = res?.accessToken || res?.idToken;
        if (!azureToken) {
          shouldStopLoading = false;
          await instance.loginRedirect(loginRequest as any);
          return;
        }

        const {
          data: { data: connectData },
        } = await api.auth.connect(azureToken);
        dispatch(loggedIn(connectData));

        const {
          data: { data: userData },
        } = await api.auth.getUserData(connectData.accessToken);
        dispatch(setUserData(userData));
      } catch (e: any) {
        const rawErrorCode = e?.errorCode || e?.error || e?.name;
        const errorCode = typeof rawErrorCode === "string" ? rawErrorCode : undefined;
        const interactiveErrorCodes = [
          "interaction_required",
          "login_required",
          "consent_required",
          // MSAL silent flow can fail via hidden iframe (e.g. X-Frame-Options / 3p cookies)
          "monitor_window_timeout",
          "block_iframe_reload",
        ];
        const isInteractionRequired =
          e instanceof InteractionRequiredAuthError ||
          (errorCode != null && interactiveErrorCodes.includes(errorCode));

        if (isInteractionRequired) {
          try {
            shouldStopLoading = false;
            await instance.acquireTokenRedirect(loginRequest as any);
            return;
          } catch (redirectErr) {
            console.error("[AuthProvider] loginRedirect failed", redirectErr);
          }
        }

        console.error("[AuthProvider] connect error", e);
        toast.error("Authentication error");
      } finally {
        if (shouldStopLoading) setLoading(false);
      }
    })().finally(() => {
      connectPromiseRef.current = null;
    });

    connectPromiseRef.current = connectPromise;
    return connectPromise;
  }, [account, acquireTokens, dispatch, instance]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (!account) {
      setLoading(false);
      return;
    }

    if (authData?.accessToken) {
      setLoading(false);
      return;
    }

    void connect();
  }, [account?.homeAccountId, inProgress, connect, authData?.accessToken]);

  if (loading) return <LinearProgress />;
  return <>{children}</>;
};

export default AuthProvider;